import { errorHelpers } from '@approvalmax/utils';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useCallback, useState } from 'react';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';
import { getAdjustedIntegrationState } from 'shared/components';
import { useConnectToReceiptBank, useDisconnectFromReceiptBank } from 'shared/data/webApp/v1';

import { messages } from './AdditionalServiceCardDext.messages';
import { AdditionalServiceCardDextProps } from './AdditionalServiceCardDext.types';

const { commonConstants, xeroConstants, qBooksConstants } = constants;

export const useAdditionalServiceCardDext = (company: AdditionalServiceCardDextProps['company']) => {
    const [isLoading, setIsLoading] = useState(false);

    const { mutate: connectToReceiptBank } = useConnectToReceiptBank();
    const { mutate: disconnectFromReceiptBank } = useDisconnectFromReceiptBank();

    const onDisconnect = useCallback(() => {
        if (company.integrationId) {
            setIsLoading(true);
            disconnectFromReceiptBank(
                {
                    body: {
                        companyId: company.id,
                        integrationId: company.integrationId,
                    },
                },
                {
                    onSettled: () => {
                        setIsLoading(false);
                    },
                }
            );
        } else {
            throw errorHelpers.invalidOperationError();
        }
    }, [company.id, company.integrationId, disconnectFromReceiptBank]);

    const onConnect = useCallback(() => {
        amplitudeService.sendData('org settings: initiate connection', {
            'connection type': 'dext',
        });

        routingService.navigateToExternalUrl(commonConstants.DEXT_CONNECTION_EXTERNAL_URL);
    }, []);

    const onReconnect = useCallback(() => {
        if (company.integrationId) {
            setIsLoading(true);

            connectToReceiptBank(
                {
                    body: {
                        companyId: company.id,
                        integrationId: company.integrationId,
                    },
                },
                {
                    onSettled: () => {
                        setIsLoading(false);
                    },
                }
            );
        } else {
            throw errorHelpers.invalidOperationError();
        }
    }, [company.id, company.integrationId, connectToReceiptBank]);

    return {
        isLoading,
        onConnect,
        onReconnect,
        onDisconnect,
    };
};

export const useGetDextDescription = (company: AdditionalServiceCardDextProps['company']) => {
    const integrationName = company.integration?.displayName;

    return company.receiptBankIntegration?.isConnected
        ? messages.connectedDescription({ integrationName })
        : messages.disconnectedDescription({ integrationName });
};

export const useGetDextLearnMoreUrl = (company: AdditionalServiceCardDextProps['company']) => {
    switch (company.integration!.integrationType) {
        case domain.IntegrationType.Xero:
            return xeroConstants.XERO_DEXT_LEARN_MORE_LINK;

        case domain.IntegrationType.QBooks:
            return qBooksConstants.QBO_DEXT_LEARN_MORE_LINK;

        case domain.IntegrationType.NetSuite:
        case domain.IntegrationType.Dear:
            return '#';

        case domain.IntegrationType.None:
            throw errorHelpers.invalidOperationError();

        default:
            throw errorHelpers.assertNever(company.integration!.integrationType);
    }
};

export const useCompanyIntegrationStatus = (company: selectors.types.ExpandedCompany) => {
    const workflows = useSelector((state) => selectors.template.getIntegrationTemplatesByCompanyId(state, company.id));
    const integration = useSelector((state) => selectors.integration.getIntegrationByCompanyId(state, company.id));

    const { isConnected, isDisconnected, isConnecting, isConnectingFinalizing, isDisconnecting } =
        getAdjustedIntegrationState(selectors.integration.getExplainedIntegrationState(integration), workflows);

    return {
        isIntegrationConnected: isConnected,
        isIntegrationConnecting: isConnecting || isConnectingFinalizing || isDisconnecting,
        isIntegrationDisconnected: isDisconnected || isConnecting,
    };
};
